import {produce} from 'immer';
import React from 'react';
import {assign, setup, fromPromise, assertEvent} from 'xstate';

import {TranslationParameters} from 'antibodies/nested-data-creation/constants/AntibodyNestedDataCreationConstants';
import {assert} from 'Asserts';
import FolderDefaults, {FolderDefaultsKey} from 'FolderDefaults';
import notifications from 'notifications';
import * as LoadingScreen from 'ui-library/LoadingScreen';
import * as logging from 'util/Logging';
import {getUserMessage, isServerRequestError} from 'util/Net';

import {AntibodyFormatGraphQL} from '../../../../graphql-fragments/antibodies/__generated/AntibodyFormatGraphQL';
import * as AntibodyNestedDataCreationSpreadsheetImportDAO from './AntibodyNestedDataCreationSpreadsheetImportDAO';
import {
  AntibodyNestedDataCreationSpreadsheetImportState,
  INITIAL_ANTIBODY_NDC_DNA_COMPONENT_SCHEMA_CONFIG,
  INITIAL_ANTIBODY_NESTED_DATA_CREATION_SPREADSHEET_IMPORT_STATE,
  NdcColumnInferenceError,
  NdcStartingType,
  DnaComponentSchemaConfig,
  DnaDomainSchemaMode,
  isNdcColumnInferenceError,
  isNdcColumnInferenceResponse,
  NdcColumnInferenceRequest,
  NdcColumnInferenceResponse,
  NdcColumnInferenceResult,
  NdcSpreadsheetImportRequest,
  NdcSpreadsheetImportValidationErrorBadRequest,
  NdcSpreadsheetInterpretation,
} from './constants/AntibodyNestedDataCreationSpreadsheetImportConstants';

type AntibodyNestedDataCreationSpreadsheetImportModalEvents =
  // Navigation
  | {type: 'next'}
  | {type: 'back'}
  // Select Format pane
  | {type: 'selectFormat'; format: AntibodyFormatGraphQL | null}
  | {type: 'selectSchema'; schemaId: string | null}
  | {type: 'setRegistryId'; registryId: string | null}
  | {type: 'setStartingType'; startingType: NdcStartingType}
  | {type: 'setTranslationParameters'; translationParameters: TranslationParameters}
  // Upload Spreadsheet pane
  | {type: 'setSpreadsheetFile'; spreadsheetFile: File}
  | {type: 'setSpreadsheetURL'; spreadsheetURL: string}
  | {type: 'clearSpreadsheet'}
  // Column Mapping pane
  | {type: 'updateSpreadsheetInterpretation'; spreadsheetInterpretation: NdcSpreadsheetInterpretation}
  // Finalize pane
  | {type: 'setProjectFolder'; projectFolderId: string}
  | {type: 'setDnaComponentSchemaConfig'; dnaComponentSchemaConfig: DnaComponentSchemaConfig}
  | {type: 'submitSpreadsheetImport'};

async function handleInferColumnsForNestedCreateAndRegisterSpreadsheet(
  input: NdcColumnInferenceRequest
): Promise<NdcColumnInferenceResponse | {errorMessage: string}> {
  try {
    const result =
      await AntibodyNestedDataCreationSpreadsheetImportDAO.inferColumnsForNestedCreateAndRegisterSpreadsheet(
        input
      );
    return result;
  } catch (e) {
    if (isServerRequestError(e)) {
      const userMessage = getUserMessage(e);
      const errorMessage = userMessage ?? 'Failed to infer column types for spreadsheet import';
      return {errorMessage};
    } else {
      logging.error(e, {extra: {message: 'Failed to infer column types for spreadsheet import'}});
      notifications.notifyError(e);
      throw e;
    }
  }
}

async function handleNestedCreateAndRegisterFromSpreadsheetImport(
  input: NdcSpreadsheetImportRequest
): Promise<void | NdcSpreadsheetImportValidationErrorBadRequest> {
  try {
    LoadingScreen.show('Creating antibodies from spreadsheet...');
    const result =
      await AntibodyNestedDataCreationSpreadsheetImportDAO.nestedCreateAndRegisterFromSpreadsheetImport(
        input
      );
    if (result.ndcWorkspaceUrl) {
      notifications.info(
        <>
          <p>We're working on creating antibodies and components...</p>
          <a href={result.ndcWorkspaceUrl}>View progress</a>
        </>
      );
    } else {
      notifications.info("We're working on creating antibodies and components...");
    }
  } catch (e) {
    // Catch BadRequestError and get the validationErrors from it, if they're set
    if (e.status === 400 && e.data?.validationErrors != null) {
      const validationErrors = e.data.validationErrors;
      return {validationErrors};
    } else {
      logging.error(e, {
        extra: {message: 'Failed to create and register antibodies from spreadsheet import'},
      });
      notifications.notifyError(e);
      throw e;
    }
  } finally {
    LoadingScreen.hide();
  }
}

/**
 * State machine handling the progress and state of importing antibodies from a spreadsheet in a modal.
 *
 * -----------------------------------------------------------------------------------------------------------
 * | State                | Purpose                                                                          |
 * |----------------------|----------------------------------------------------------------------------------|
 * | selectFormat         | User picks antibody format, schema, registry, starting type (domains/chains/pairs)|
 * | uploadSpreadsheet    | User uploads a spreadsheet file (stored locally + S3 URL), backend infers column |
 * |                      | types from headers and first row and displays errors requiring reupload          |
 * | columnMapping        | User reviews/picks inferred column types for each spreadsheet column when        |
 * |                      | multiple inferred types are present                                              |
 * | finalize             | User picks project folder and DNA component schemas                              |
 * | saving               | Loading state while backend validates and kicks off the pipeline                 |
 * | validationErrors     | Shows validation errors thrown during validation (e.g. missing entities)         |
 * | done                 | Modal closes since pipeline was successfully kicked off                          |
 * ---------------------------------------------------------------------------------------------------------*/
const AntibodyNestedDataCreationModalMachine = setup({
  types: {
    context: {} as AntibodyNestedDataCreationSpreadsheetImportState,
    events: {} as AntibodyNestedDataCreationSpreadsheetImportModalEvents,
    input: {} as {initialProjectFolderId?: string | null},
  },
  actors: {
    inferColumnTypes: fromPromise(
      async ({
        input,
      }: {
        input: NdcColumnInferenceRequest;
      }): Promise<NdcColumnInferenceResponse | {errorMessage: string}> =>
        await handleInferColumnsForNestedCreateAndRegisterSpreadsheet(input)
    ),
    submitSpreadsheetImport: fromPromise(
      async ({input}: {input: NdcSpreadsheetImportRequest}) =>
        await handleNestedCreateAndRegisterFromSpreadsheetImport(input)
    ),
  },
  actions: {
    // Select Format pane
    selectFormat: assign(({context, event}) => {
      assertEvent(event, 'selectFormat');
      const formatId = event.format?.id ?? null;
      return produce(context, (draft) => {
        draft.selectedFormatId = formatId;
      });
    }),
    selectSchema: assign(({context, event}) => {
      assertEvent(event, 'selectSchema');
      if (context.selectedSchemaId === event.schemaId) {
        return context;
      }
      return produce(context, (draft) => {
        draft.selectedSchemaId = event.schemaId;
        draft.spreadsheetInterpretation = null;
        draft.dnaComponentSchemaConfig = {...INITIAL_ANTIBODY_NDC_DNA_COMPONENT_SCHEMA_CONFIG};
      });
    }),
    setRegistryId: assign(({context, event}) => {
      assertEvent(event, 'setRegistryId');
      return produce(context, (draft) => {
        draft.selectedRegistryId = event.registryId;
      });
    }),
    setStartingType: assign(({context, event}) => {
      assertEvent(event, 'setStartingType');
      if (context.selectedStartingType === event.startingType) {
        return context;
      }
      return produce(context, (draft) => {
        draft.selectedStartingType = event.startingType;
        draft.spreadsheetInterpretation = null;
        draft.dnaComponentSchemaConfig = {...INITIAL_ANTIBODY_NDC_DNA_COMPONENT_SCHEMA_CONFIG};
      });
    }),
    setTranslationParameters: assign(({context, event}) => {
      assertEvent(event, 'setTranslationParameters');
      return produce(context, (draft) => {
        draft.translationParameters = event.translationParameters;
      });
    }),
    // Upload Spreadsheet pane
    setSpreadsheetFile: assign(({context, event}) => {
      assertEvent(event, 'setSpreadsheetFile');
      return produce(context, (draft) => {
        draft.spreadsheetFile = event.spreadsheetFile;
        draft.spreadsheetInterpretation = null;
        draft.columnInferenceErrorMessage = null;
      });
    }),
    setSpreadsheetURL: assign(({context, event}) => {
      assertEvent(event, 'setSpreadsheetURL');
      return produce(context, (draft) => {
        draft.spreadsheetURL = event.spreadsheetURL;
        draft.spreadsheetInterpretation = null;
        draft.columnInferenceErrorMessage = null;
      });
    }),
    clearSpreadsheet: assign(({context, event}) => {
      assertEvent(event, 'clearSpreadsheet');
      return produce(context, (draft) => {
        draft.spreadsheetFile = null;
        draft.spreadsheetURL = null;
        draft.spreadsheetInterpretation = null;
        draft.columnInferenceErrorMessage = null;
      });
    }),
    // Column Mapping pane
    updateSpreadsheetInterpretation: assign(({context, event}) => {
      assertEvent(event, 'updateSpreadsheetInterpretation');
      return produce(context, (draft) => {
        draft.spreadsheetInterpretation = event.spreadsheetInterpretation;
      });
    }),
    // Finalize pane
    setProjectFolder: assign(({context, event}) => {
      assertEvent(event, 'setProjectFolder');
      return produce(context, (draft) => {
        draft.selectedProjectFolderId = event.projectFolderId;
      });
    }),
    setDnaComponentSchemaConfig: assign(({context, event}) => {
      assertEvent(event, 'setDnaComponentSchemaConfig');
      return produce(context, (draft) => {
        draft.dnaComponentSchemaConfig = event.dnaComponentSchemaConfig;
      });
    }),
  },
}).createMachine({
  context: ({input}) =>
    produce(INITIAL_ANTIBODY_NESTED_DATA_CREATION_SPREADSHEET_IMPORT_STATE, (draft) => {
      draft.selectedProjectFolderId =
        input.initialProjectFolderId ?? FolderDefaults.get(FolderDefaultsKey.INVENTORY) ?? null;
    }),
  id: 'antibodyNestedDataCreationSpreadsheetImportModal',
  initial: 'selectFormat',
  states: {
    selectFormat: {
      on: {
        next: {
          target: 'uploadSpreadsheet',
          guard: ({context}) => {
            if (context.selectedFormatId == null) {
              logging.info('Cannot proceed: selectedFormatId is missing');
              return false;
            }
            if (context.selectedSchemaId == null) {
              logging.info('Cannot proceed: selectedSchemaId is missing');
              return false;
            }
            if (context.selectedRegistryId == null) {
              logging.info('Cannot proceed: selectedRegistryId is missing');
              return false;
            }
            if (context.selectedStartingType == null) {
              logging.info('Cannot proceed: selectedStartingType is missing');
              return false;
            }
            return true;
          },
        },
        selectFormat: {actions: 'selectFormat'},
        selectSchema: {actions: 'selectSchema'},
        setRegistryId: {actions: 'setRegistryId'},
        setStartingType: {actions: 'setStartingType'},
        // TBD where this will actually be set.
        setTranslationParameters: {actions: 'setTranslationParameters'},
      },
    },
    uploadSpreadsheet: {
      initial: 'selectingSpreadsheet',
      on: {
        next: {
          target: 'columnMapping',
          guard: ({context}) => {
            if (context.spreadsheetFile == null) {
              logging.info('Cannot proceed: spreadsheetFile is missing');
              return false;
            }
            if (context.spreadsheetURL == null) {
              logging.info('Cannot proceed: spreadsheetURL is missing');
              return false;
            }
            if (context.spreadsheetInterpretation == null) {
              logging.info('Cannot proceed: spreadsheetInterpretation is missing');
              return false;
            }
            if (context.columnInferenceErrorMessage != null) {
              logging.info('Cannot proceed: column inference error present');
              return false;
            }
            return true;
          },
        },
        back: {
          target: 'selectFormat',
        },
      },
      states: {
        selectingSpreadsheet: {
          always: {
            target: 'inferringColumns',
            guard: ({context}) =>
              context.spreadsheetFile != null &&
              context.spreadsheetURL != null &&
              context.spreadsheetInterpretation == null &&
              context.columnInferenceErrorMessage == null,
          },
          on: {
            setSpreadsheetFile: {actions: 'setSpreadsheetFile'},
            setSpreadsheetURL: {actions: 'setSpreadsheetURL'},
            clearSpreadsheet: {actions: 'clearSpreadsheet'},
          },
        },
        inferringColumns: {
          invoke: {
            src: 'inferColumnTypes',
            input: ({
              context,
            }: {
              context: AntibodyNestedDataCreationSpreadsheetImportState;
            }): NdcColumnInferenceRequest => {
              assert(context.spreadsheetURL != null, 'Spreadsheet URL should be set');
              assert(context.selectedSchemaId != null, 'Schema ID should be set');
              return {
                spreadsheetLocation: context.spreadsheetURL,
                antibodySchemaId: context.selectedSchemaId,
                componentFormatType: context.selectedStartingType,
              };
            },
            onDone: [
              {
                target: 'columnsInferred',
                guard: ({event}) => {
                  if (isNdcColumnInferenceResponse(event.output)) {
                    return true;
                  } else {
                    logging.info('Cannot proceed: column inference returned a spreadsheet-wide error');
                    return false;
                  }
                },
                actions: assign(({context, event}) => {
                  assert(
                    isNdcColumnInferenceResponse(event.output),
                    'Action guard should prevent action from running if column inference returned an error'
                  );
                  const columnInferenceResponse = event.output;
                  const spreadsheetInterpretation: NdcSpreadsheetInterpretation = {
                    columns: columnInferenceResponse.columnTypes.map(
                      (column: NdcColumnInferenceResult | NdcColumnInferenceError) => {
                        if (isNdcColumnInferenceError(column)) {
                          return {
                            columnIndex: column.columnIndex,
                            columnName: column.columnName,
                            componentFormatKey: null,
                            errorMessage: column.errorMessage,
                            inferredTypes: null,
                            selectedType: null,
                          };
                        } else {
                          return {
                            columnIndex: column.columnIndex,
                            columnName: column.columnName,
                            componentFormatKey: column.componentFormatKey,
                            errorMessage: null,
                            inferredTypes: column.inferredComponentTypes,
                            selectedType: null,
                          };
                        }
                      }
                    ),
                    rowCount: columnInferenceResponse.rowCount,
                  };

                  return produce(context, (draft) => {
                    draft.spreadsheetInterpretation = spreadsheetInterpretation;
                  });
                }),
              },
              {
                target: 'showingError',
                guard: ({event}) => !isNdcColumnInferenceResponse(event.output),
                actions: assign(({context, event}) => {
                  assert(
                    !isNdcColumnInferenceResponse(event.output),
                    'Action guard should prevent action from running if column inference returned an error'
                  );
                  const errorMessage = event.output.errorMessage;
                  return produce(context, (draft) => {
                    draft.columnInferenceErrorMessage = errorMessage;
                  });
                }),
              },
            ],
            onError: {
              target: 'showingError',
            },
          },
          on: {
            clearSpreadsheet: {
              target: 'selectingSpreadsheet',
              actions: 'clearSpreadsheet',
            },
          },
        },
        columnsInferred: {
          on: {
            clearSpreadsheet: {
              target: 'selectingSpreadsheet',
              actions: 'clearSpreadsheet',
            },
          },
        },
        showingError: {
          on: {
            clearSpreadsheet: {
              target: 'selectingSpreadsheet',
              actions: 'clearSpreadsheet',
            },
          },
        },
      },
    },
    columnMapping: {
      on: {
        next: {
          target: 'finalize',
          guard: ({context}) => {
            if (context.spreadsheetInterpretation == null) {
              logging.info('Cannot proceed: spreadsheetInterpretation is missing');
              return false;
            }
            // Every column must have either an inferred type that was inferred by the backend or a
            // user-selected type
            const allColumnsHaveType = context.spreadsheetInterpretation.columns.every(
              (column) =>
                column.selectedType != null ||
                (column.inferredTypes != null && column.inferredTypes.length === 1)
            );
            if (!allColumnsHaveType) {
              logging.info('Cannot proceed: not all columns have a selected or inferred type');
              return false;
            }
            return true;
          },
        },
        back: {
          target: 'uploadSpreadsheet',
        },
        updateSpreadsheetInterpretation: {actions: 'updateSpreadsheetInterpretation'},
      },
    },
    finalize: {
      on: {
        back: {
          target: 'columnMapping',
        },
        submitSpreadsheetImport: {
          target: 'saving',
          guard: ({context}) => {
            if (context.selectedProjectFolderId == null) {
              logging.info('Cannot proceed: selectedProjectFolderId is missing');
              return false;
            }
            if (
              context.dnaComponentSchemaConfig.dnaDomainSchemaMode === DnaDomainSchemaMode.SHARED_SCHEMA &&
              context.dnaComponentSchemaConfig.dnaDomainSchemaId == null
            ) {
              logging.info('Cannot proceed: dnaDomainSchemaId is missing for SHARED_SCHEMA mode');
              return false;
            }
            if (
              context.dnaComponentSchemaConfig.dnaDomainSchemaMode ===
                DnaDomainSchemaMode.SEPARATE_VARIABLE_AND_CONSTANT_SCHEMAS &&
              (context.dnaComponentSchemaConfig.dnaDomainVariableSchemaId == null ||
                context.dnaComponentSchemaConfig.dnaDomainConstantSchemaId == null)
            ) {
              if (context.dnaComponentSchemaConfig.dnaDomainVariableSchemaId == null) {
                logging.info(
                  'Cannot proceed: dnaDomainVariableSchemaId is missing for SEPARATE_VARIABLE_AND_CONSTANT_SCHEMAS mode'
                );
              }
              if (context.dnaComponentSchemaConfig.dnaDomainConstantSchemaId == null) {
                logging.info(
                  'Cannot proceed: dnaDomainConstantSchemaId is missing for SEPARATE_VARIABLE_AND_CONSTANT_SCHEMAS mode'
                );
              }
              return false;
            }
            if (context.dnaComponentSchemaConfig.dnaChainSchemaId == null) {
              logging.info('Cannot proceed: dnaChainSchemaId is missing');
              return false;
            }
            return true;
          },
        },
        setProjectFolder: {actions: 'setProjectFolder'},
        setDnaComponentSchemaConfig: {actions: 'setDnaComponentSchemaConfig'},
      },
    },
    saving: {
      invoke: {
        src: 'submitSpreadsheetImport',
        input: ({
          context,
        }: {
          context: AntibodyNestedDataCreationSpreadsheetImportState;
        }): NdcSpreadsheetImportRequest => {
          assert(context.selectedProjectFolderId != null, 'Project folder ID should be set');
          assert(context.selectedSchemaId != null, 'Schema ID should be set');
          assert(context.spreadsheetURL != null, 'Spreadsheet URL should be set');
          assert(context.selectedStartingType != null, 'Starting type should be set');
          assert(context.spreadsheetInterpretation != null, 'Spreadsheet interpretation should be set');
          return {
            projectFolderId: context.selectedProjectFolderId,
            antibodySchemaId: context.selectedSchemaId,
            spreadsheetLocation: context.spreadsheetURL,
            componentType: context.selectedStartingType,
            columnTypes: context.spreadsheetInterpretation.columns.map((column) => {
              const finalType =
                column.selectedType ?? (column.inferredTypes?.length === 1 ? column.inferredTypes[0] : null);
              assert(finalType != null, 'Column should have a selected type or exactly one inferred type');

              return {
                columnIndex: column.columnIndex,
                columnName: column.columnName,
                componentFormatKey: column.componentFormatKey!,
                ...finalType,
              };
            }),
            translationParameters: context.translationParameters,
          };
        },
        onDone: [
          {
            target: 'validationErrors',
            guard: ({event}) =>
              event.output?.validationErrors != null && event.output.validationErrors.length > 0,
          },
          {
            target: 'done',
            guard: ({event}) =>
              event.output?.validationErrors == null || event.output.validationErrors.length === 0,
          },
        ],
        onError: {
          target: 'finalize',
        },
      },
    },
    validationErrors: {
      on: {
        back: {
          target: 'finalize',
        },
      },
    },
    done: {
      type: 'final',
    },
  },
});

export default AntibodyNestedDataCreationModalMachine;
